import React, { useState, useEffect, useRef } from "react";

const colors = {
  mainBlue: "#061941",
  cafeNoir: "#583E23",
  carrieban: "#3C6E71",
  isabelline: "#F4F3EE",
  gold: "#F2AF29"
};

const privacySections = [
  {
    id: 1,
    title: "Information We Collect",
    content: "We may collect personal information (name, email, payment details), usage data (IP address, browser type), and cookies/tracking information."
  },
  {
    id: 2,
    title: "How We Use Your Information",
    content: "We use data to provide and improve services, process transactions, personalize experiences, send updates, and prevent fraud/security risks."
  },
  {
    id: 3,
    title: "Sharing Your Information",
    content: "We may share data with service providers (e.g., hosting, payments), legal authorities if required, and during business transfers. We do not sell or rent data."
  },
  {
    id: 4,
    title: "Your Rights",
    content: "Depending on your jurisdiction (GDPR, CCPA), you may request data access, updates, deletion, portability, or opt out of marketing."
  },
  {
    id: 5,
    title: "Data Retention",
    content: "We retain information only as long as necessary for services, legal compliance, or dispute resolution."
  },
  {
    id: 6,
    title: "Security",
    content: "We use industry-standard measures to protect your data, but no system is completely secure."
  },
  {
    id: 7,
    title: "Children's Privacy",
    content: "Our Website is not intended for children under 13 (or 16 where applicable). We do not knowingly collect data from children."
  },
  {
    id: 8,
    title: "Third-Party Links",
    content: "We are not responsible for privacy practices of third-party sites linked on our Website."
  },
  {
    id: 9,
    title: "International Transfers",
    content: "If you access our Website outside the United States, your data may be processed in other jurisdictions with different laws."
  },
  {
    id: 10,
    title: "Changes to Policy",
    content: "We may update this Privacy Policy occasionally. Continued use of the Website constitutes acceptance."
  },
  {
    id: 11,
    title: "Contact Us",
    content: "📧 Email: neuhawkai@gmail.com"
  }
];

export default function Privacy() {
  const [visibleSections, setVisibleSections] = useState([]);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = parseInt(entry.target.dataset.sectionId);
            setVisibleSections(prev => 
              prev.includes(sectionId) ? prev : [...prev, sectionId]
            );
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(120deg, ${colors.mainBlue} 70%, ${colors.gold} 95%, ${colors.gold} 100%)`,
        fontFamily: "Segoe UI, Arial, sans-serif",
        paddingBottom: "7rem"
      }}
    >
      {/* Header */}
      <header
        style={{
          padding: "2.3rem 1rem 1.7rem 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.2rem"
        }}
      >
        <h1
          style={{
            fontFamily: "Poppins, Segoe UI, Arial, sans-serif",
            fontSize: "2.4rem",
            fontWeight: 800,
            letterSpacing: "2px",
            color: colors.isabelline,
            margin: 0,
            lineHeight: 1.1
          }}
        >
          Privacy Policy
        </h1>
      </header>

      {/* Content Container */}
      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          padding: "0 1.5rem"
        }}
      >
        {/* Date Info */}
        <div
          style={{
            background: colors.isabelline,
            borderRadius: "16px",
            padding: "1.5rem",
            marginBottom: "2rem",
            boxShadow: "0 4px 24px 0 rgba(88, 62, 35, 0.08)",
            border: `2px solid ${colors.gold}`,
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Yellow accent line */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${colors.gold}, ${colors.xanthous || colors.gold})`
            }}
          />
          <p style={{ margin: "0 0 0.5rem 0", color: colors.mainBlue, fontWeight: 600 }}>
            <strong>Effective Date:</strong> 08/17/2025
          </p>
          <p style={{ margin: 0, color: colors.mainBlue, fontWeight: 600 }}>
            <strong>Last Updated:</strong> 08/17/2025
          </p>
        </div>

        {/* Privacy Policy Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {privacySections.map((section, index) => (
            <div
              key={section.id}
              ref={el => sectionRefs.current[index] = el}
              data-section-id={section.id}
              style={{
                background: colors.isabelline,
                borderRadius: "16px",
                padding: "1.5rem",
                boxShadow: "0 4px 24px 0 rgba(88, 62, 35, 0.08)",
                border: `2px solid ${colors.gold}`,
                position: "relative",
                overflow: "hidden",
                opacity: visibleSections.includes(section.id) ? 1 : 0,
                transform: visibleSections.includes(section.id) 
                  ? "translateY(0) scale(1)" 
                  : "translateY(30px) scale(0.95)",
                transition: "all 0.6s cubic-bezier(0.61, 0.09, 0.66, 1.2)",
                transitionDelay: `${index * 0.1}s`
              }}
            >
              {/* Yellow accent line */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${colors.gold}, ${colors.xanthous || colors.gold})`
                }}
              />
              
              {/* Section number with gold background */}
              <div
                style={{
                  position: "absolute",
                  top: "1rem",
                  left: "1rem",
                  background: colors.gold,
                  color: colors.mainBlue,
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  boxShadow: `0 2px 8px 0 ${colors.gold}40`
                }}
              >
                {section.id}
              </div>

              <h2 style={{
                fontSize: "1.4rem",
                fontWeight: 700,
                color: colors.mainBlue,
                margin: "0 0 1rem 0",
                fontFamily: "Poppins, Segoe UI, Arial, sans-serif",
                paddingLeft: "3rem"
              }}>
                {section.title}
              </h2>
              <p style={{
                fontSize: "1rem",
                color: colors.mainBlue,
                lineHeight: 1.6,
                margin: 0,
                opacity: 0.9
              }}>
                {section.content}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
